/**
 * Cálculo de troco do PDV.
 *
 * Mora fora do componente porque é aritmética de dinheiro: erro aqui é
 * dinheiro a mais ou a menos na mão do cliente, e isso merece teste.
 */

/** Cédulas em circulação no Brasil, das menores para as maiores. */
const CEDULAS = [2, 5, 10, 20, 50, 100, 200];

/** Quantas sugestões mostrar antes de virar um teclado numérico. */
const MAX_SUGESTOES = 5;

/** Centavos são inteiros; reais em ponto flutuante acumulam erro. */
const emCentavos = (valor: number) => Math.round(valor * 100);

export function calcularTroco(total: number, recebido: number | null): number {
  return (emCentavos(recebido ?? 0) - emCentavos(total)) / 100;
}

/**
 * O que o cliente plausivelmente entrega: o valor exato, o arredondamento
 * para cima mais próximo, e as cédulas que cobrem o total.
 *
 * Poupar a conta de cabeça é o ponto — numa fila, é onde o troco sai errado.
 */
export function sugestoesDePagamento(total: number): number[] {
  if (total <= 0) return [];

  const candidatos = new Set<number>([total]);

  // Arredondamentos para cima: o cliente completa com moedas ou nota menor.
  for (const passo of [5, 10, 50]) {
    const arredondado = Math.ceil(total / passo) * passo;
    if (arredondado > total) candidatos.add(arredondado);
  }

  for (const cedula of CEDULAS) {
    if (cedula > total) candidatos.add(cedula);
  }

  return [...candidatos].sort((a, b) => a - b).slice(0, MAX_SUGESTOES);
}

/**
 * Lê o que o caixa digitou.
 *
 * Aceita vírgula (é o que o teclado brasileiro produz), ponto, prefixo de
 * moeda e separador de milhar — recusar qualquer um deles seria implicância
 * com quem está atendendo.
 */
export function parseValorDigitado(texto: string): number | null {
  const limpo = texto.replace(/[^\d,.]/g, '').trim();
  if (!limpo) return null;

  // Com os dois separadores presentes, o último é o decimal.
  const ultimaVirgula = limpo.lastIndexOf(',');
  const ultimoPonto = limpo.lastIndexOf('.');
  const posicaoDecimal = Math.max(ultimaVirgula, ultimoPonto);

  const normalizado =
    posicaoDecimal === -1
      ? limpo
      : `${limpo.slice(0, posicaoDecimal).replace(/[,.]/g, '')}.${limpo.slice(posicaoDecimal + 1).replace(/[,.]/g, '')}`;

  const numero = Number.parseFloat(normalizado);
  return Number.isFinite(numero) ? numero : null;
}
